import { useCallback, useEffect, useMemo, useState } from 'react';

import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  buildAdName,
  createAdNameOption,
  deleteAdNameOption,
  listAdNameHistory,
  listAdNameOptions,
} from '@src/api/adNameBuilder';
import { useAuth } from '@src/context';
import { useMessage } from '@src/context/common';

const OPTION_TYPES = {
  theme: 'theme',
  subtheme: 'subtheme',
  adType: 'ad_type',
};

const filter = createFilterOptions();

const normalizeOption = (option) => ({
  id: option.id,
  value: option.value,
  label: option.label || option.value,
});

const hasOptionValue = (options, value) =>
  options.some((option) => option.value.toLowerCase() === value.toLowerCase());

const sanitizeAdPart = (value) => value.replace(/[&=]/g, '');

export default function AdNameBuilderPage() {
  const message = useMessage();
  const { state: authState } = useAuth();
  const user = authState?.user || {};
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);
  const [themeOptions, setThemeOptions] = useState([]);
  const [subthemeOptions, setSubthemeOptions] = useState([]);
  const [adTypeOptions, setAdTypeOptions] = useState([]);
  const [theme, setTheme] = useState(null);
  const [subtheme, setSubtheme] = useState(null);
  const [adType, setAdType] = useState(null);
  const [notes, setNotes] = useState('');
  const [adName, setAdName] = useState('');
  const [historyQuery, setHistoryQuery] = useState('');
  const [historyResults, setHistoryResults] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [building, setBuilding] = useState(false);
  const canBuild = useMemo(() => {
    const themeValue = (theme?.value || theme || '').trim();
    const subthemeValue = (subtheme?.value || subtheme || '').trim();
    const adTypeValue = (adType?.value || adType || '').trim();
    return Boolean(themeValue && adTypeValue);
  }, [adType, subtheme, theme]);

  const fetchOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const [themesRes, subthemesRes, adTypesRes] = await Promise.all([
        listAdNameOptions(OPTION_TYPES.theme),
        listAdNameOptions(OPTION_TYPES.subtheme),
        listAdNameOptions(OPTION_TYPES.adType),
      ]);
      setThemeOptions((themesRes?.data || []).map(normalizeOption));
      setSubthemeOptions((subthemesRes?.data || []).map(normalizeOption));
      setAdTypeOptions((adTypesRes?.data || []).map(normalizeOption));
    } catch (error) {
      console.error('Failed to load ad name options', error);
      message?.error?.('Failed to load ad name options.');
    } finally {
      setLoadingOptions(false);
    }
  }, [message]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  const fetchHistory = useCallback(
    async (query = historyQuery) => {
      setHistoryLoading(true);
      try {
        const res = await listAdNameHistory(query ? { q: query } : {});
        setHistoryResults(res?.data || []);
      } catch (error) {
        console.error('Failed to load ad name history', error);
        message?.error?.('Failed to load ad name history.');
      } finally {
        setHistoryLoading(false);
      }
    },
    [historyQuery, message],
  );

  useEffect(() => {
    fetchHistory('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createOption = async (optionType, inputValue) => {
    const trimmed = sanitizeAdPart(inputValue.trim());
    if (!trimmed) return;
    const gatedTypes = [OPTION_TYPES.theme, OPTION_TYPES.subtheme];
    if (gatedTypes.includes(optionType) && !isStaff) {
      message?.error?.('Only staff can add new options.');
      return;
    }
    try {
      const res = await createAdNameOption({
        option_type: optionType,
        label: trimmed,
        value: trimmed,
      });
      if (res?.data) {
        applyCreatedOption(optionType, normalizeOption(res.data));
      }
    } catch (error) {
      console.error('Failed to create ad name option', error);
      message?.error?.('Could not save the new option.');
    }
  };

  const handleAutocompleteChange = (optionType, newValue, setSelection) => {
    if (typeof newValue === 'string') {
      const cleaned = sanitizeAdPart(newValue);
      createOption(optionType, cleaned);
      setSelection(cleaned);
      return;
    }

    if (newValue?.inputValue) {
      const cleaned = sanitizeAdPart(newValue.inputValue);
      createOption(optionType, cleaned);
      setSelection(cleaned);
      return;
    }

    setSelection(newValue?.value || '');
  };

  const handleOpenDelete = (optionType, option) => {
    if (!option?.id) return;
    setDeleteTarget({ optionType, option });
    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.option?.id) return;
    try {
      await deleteAdNameOption(deleteTarget.option.id);
      const { optionType, option } = deleteTarget;
      if (optionType === OPTION_TYPES.theme) {
        setThemeOptions((prev) => prev.filter((item) => item.id !== option.id));
        if ((theme?.value || theme) === option.value) {
          setTheme(null);
        }
      } else if (optionType === OPTION_TYPES.subtheme) {
        setSubthemeOptions((prev) => prev.filter((item) => item.id !== option.id));
        if ((subtheme?.value || subtheme) === option.value) {
          setSubtheme(null);
        }
      } else if (optionType === OPTION_TYPES.adType) {
        setAdTypeOptions((prev) => prev.filter((item) => item.id !== option.id));
        if ((adType?.value || adType) === option.value) {
          setAdType(null);
        }
      }
    } catch (error) {
      console.error('Failed to delete ad name option', error);
      message?.error?.('Could not delete the option.');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const canDeleteOption = (optionType) => optionType === OPTION_TYPES.adType || isStaff;

  const applyCreatedOption = (optionType, created) => {
    if (!created) return;
    if (optionType === OPTION_TYPES.theme) {
      setThemeOptions((prev) => (hasOptionValue(prev, created.value) ? prev : [...prev, created]));
    } else if (optionType === OPTION_TYPES.subtheme) {
      setSubthemeOptions((prev) => (hasOptionValue(prev, created.value) ? prev : [...prev, created]));
    } else if (optionType === OPTION_TYPES.adType) {
      setAdTypeOptions((prev) => (hasOptionValue(prev, created.value) ? prev : [...prev, created]));
    }
  };

  const handleBuild = async () => {
    const themeValue = sanitizeAdPart((theme?.value || theme || '').trim());
    const subthemeValue = sanitizeAdPart((subtheme?.value || subtheme || '').trim());
    const adTypeValue = sanitizeAdPart((adType?.value || adType || '').trim());
    const notesValue = sanitizeAdPart((notes || '').trim());

    if (!canBuild) {
      message?.error?.('Theme, subtheme, and ad type are required.');
      return;
    }

    setBuilding(true);
    try {
      const res = await buildAdName({
        theme: themeValue,
        subtheme: subthemeValue,
        ad_type: adTypeValue,
        notes: notesValue,
      });
      setAdName(res?.data?.ad_name || '');
      if (res?.data?.history) {
        setHistoryResults((prev) => [res.data.history, ...prev].slice(0, 20));
      }
    } catch (error) {
      console.error('Failed to build ad name', error);
      message?.error?.('Failed to build ad name.');
    } finally {
      setBuilding(false);
    }
  };

  const copyText = async (value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      message?.success?.('Ad name copied to clipboard.');
    } catch (error) {
      console.error('Failed to copy ad name', error);
      message?.error?.('Could not copy the ad name.');
    }
  };

  return (
    <Box sx={{ flex: '1 1 auto', p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Ad Name Builder
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Build ad names in the format: <strong>s=</strong>Theme &amp; <strong>st=</strong>Subtheme (optional) &amp;{' '}
            <strong>at=</strong>Ad Type &amp; <strong>notes=</strong>Notes
          </Typography>
        </Box>

        <Card>
          <CardHeader title="Ad Name Fields" />
          <Divider />
          <CardContent>
            <Stack spacing={2}>
              <Autocomplete
                value={theme}
                options={themeOptions}
                loading={loadingOptions}
                freeSolo
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);
                  const { inputValue } = params;
                  const exists = inputValue && hasOptionValue(options, inputValue);
                  if (inputValue !== '' && !exists) {
                    filtered.push({
                      inputValue,
                      label: `Add "${inputValue}"`,
                    });
                  }
                  return filtered;
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option.inputValue) return option.inputValue;
                  return option.label || option.value || '';
                }}
                isOptionEqualToValue={(option, value) =>
                  typeof value === 'string' ? option.value === value : option.value === value?.value
                }
                onChange={(event, newValue) =>
                  handleAutocompleteChange(OPTION_TYPES.theme, newValue, setTheme)
                }
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <span>{option.label || option.value}</span>
                      {option.id && canDeleteOption(OPTION_TYPES.theme) ? (
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenDelete(OPTION_TYPES.theme, option);
                          }}
                        >
                          <span aria-hidden="true">×</span>
                        </IconButton>
                      ) : null}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Theme (s=)"
                    placeholder="Select or add a theme"
                    helperText="Characters '&' and '=' are not allowed."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingOptions ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                value={subtheme}
                options={subthemeOptions}
                loading={loadingOptions}
                freeSolo
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);
                  const { inputValue } = params;
                  const exists = inputValue && hasOptionValue(options, inputValue);
                  if (inputValue !== '' && !exists) {
                    filtered.push({
                      inputValue,
                      label: `Add "${inputValue}"`,
                    });
                  }
                  return filtered;
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option.inputValue) return option.inputValue;
                  return option.label || option.value || '';
                }}
                isOptionEqualToValue={(option, value) =>
                  typeof value === 'string' ? option.value === value : option.value === value?.value
                }
                onChange={(event, newValue) =>
                  handleAutocompleteChange(OPTION_TYPES.subtheme, newValue, setSubtheme)
                }
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <span>{option.label || option.value}</span>
                      {option.id && canDeleteOption(OPTION_TYPES.subtheme) ? (
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenDelete(OPTION_TYPES.subtheme, option);
                          }}
                        >
                          <span aria-hidden="true">×</span>
                        </IconButton>
                      ) : null}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Subtheme (st=)"
                    placeholder="Select or add a subtheme"
                    helperText="Optional. Characters '&' and '=' are not allowed."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingOptions ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                value={adType}
                options={adTypeOptions}
                loading={loadingOptions}
                freeSolo
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);
                  const { inputValue } = params;
                  const exists = inputValue && hasOptionValue(options, inputValue);
                  if (inputValue !== '' && !exists) {
                    filtered.push({
                      inputValue,
                      label: `Add "${inputValue}"`,
                    });
                  }
                  return filtered;
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option.inputValue) return option.inputValue;
                  return option.label || option.value || '';
                }}
                isOptionEqualToValue={(option, value) =>
                  typeof value === 'string' ? option.value === value : option.value === value?.value
                }
                onChange={(event, newValue) =>
                  handleAutocompleteChange(OPTION_TYPES.adType, newValue, setAdType)
                }
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <span>{option.label || option.value}</span>
                      {option.id && canDeleteOption(OPTION_TYPES.adType) ? (
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenDelete(OPTION_TYPES.adType, option);
                          }}
                        >
                          <span aria-hidden="true">×</span>
                        </IconButton>
                      ) : null}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ad Type (at=)"
                    placeholder="Select or add an ad type"
                    helperText="Characters '&' and '=' are not allowed."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingOptions ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <TextField
                label="Notes (notes=)"
                value={notes}
                onChange={(event) => setNotes(sanitizeAdPart(event.target.value))}
                placeholder="Optional notes"
                helperText="Optional. Characters '&' and '=' are not allowed."
              />

              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="contained" onClick={handleBuild} disabled={!canBuild || building}>
                  {building ? 'Building…' : 'Generate ad name'}
                </Button>
                <Button variant="text" onClick={fetchOptions} disabled={loadingOptions}>
                  Refresh options
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Generated Ad Name" />
          <Divider />
          <CardContent>
            <TextField
              fullWidth
              label="Ad name"
              value={adName}
              placeholder="Generate to see the formatted name"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => copyText(adName)} disabled={!adName} edge="end">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader title="Find Existing Ad Names" />
          <Divider />
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  fullWidth
                  label="Search ad names"
                  value={historyQuery}
                  onChange={(event) => setHistoryQuery(event.target.value)}
                  placeholder="Search by ad name"
                />
                <Button variant="outlined" onClick={() => fetchHistory(historyQuery)} disabled={historyLoading}>
                  Search
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setHistoryQuery('');
                    fetchHistory('');
                  }}
                  disabled={historyLoading}
                >
                  Clear
                </Button>
              </Stack>

              <List dense>
                {historyResults.map((entry) => (
                  <ListItem
                    key={entry.id}
                    divider
                    secondaryAction={(
                      <IconButton edge="end" onClick={() => copyText(entry.ad_name)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    )}
                    onClick={() => setAdName(entry.ad_name)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <ListItemText
                      primary={entry.ad_name}
                      secondary={entry.created_at ? new Date(entry.created_at).toLocaleString() : ''}
                    />
                  </ListItem>
                ))}
                {!historyLoading && historyResults.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No ad names found." />
                  </ListItem>
                ) : null}
              </List>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
      <Dialog open={deleteDialogOpen} onClose={handleCloseDelete} maxWidth="sm" fullWidth>
        <DialogTitle>Delete option?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete this option?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCloseDelete}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
          >
            Delete option
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
